import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Observable, finalize, takeUntil } from 'rxjs';

import {
  ContactsByCellPhoneDto,
  CustomResult,
  CustomResultOfPaginatedListOfContactsByCellPhoneDto,
  GetEnumValuesDto,
  PaginatedListOfContactsByCellPhoneDto,
  UpdateContactIdRequest,
} from 'src/app/core/api/models';
import {
  ContactsService,
  OutBoxMessagesService,
} from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { FormPageConfig } from 'src/app/core/models/form-page-config.model';
import { ConsoleService } from 'src/app/core/services/console.service';
import { MobileNumberService } from 'src/app/core/services/mobile-number.service';
import { BaseIndexPageComponent } from 'src/app/pages/shared/base/components/base-index-page.component';
import { ContactsByCellPhoneFormComponent } from './contacts-by-cellphone-form/contacts-by-cellphone-form.component';
import { BaseIndexModule } from 'src/app/pages/shared/base/modules/base-index.module';
import { PageNames } from 'src/app/configs/page-names.enum';


@Component({
    selector: 'app-contacts-by-cellphone',
    templateUrl: './contacts-by-cellphone.component.html',
    imports: [BaseIndexModule, ReactiveFormsModule, ContactsByCellPhoneFormComponent]
})
export class ContactsByCellPhoneComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties
  pageName = PageNames.contactsByCellPhone;

  modelWithPagination$: Observable<PaginatedListOfContactsByCellPhoneDto>;
  items$: Observable<Array<ContactsByCellPhoneDto>>;
  formPageConfig: FormPageConfig<any> = {};
  ContactCreateTypeEnumValues: GetEnumValuesDto[] = [];
  GenderTypeEnumValues: GetEnumValuesDto[] = [];
  PhonebookTypeEnumValues: GetEnumValuesDto[] = [];
  mobileNumber: string;
  selectedContacts: Array<UpdateContactIdRequest> = [];

  //#endregion

  constructor(
    protected currentService: ContactsService,
    public outBoxMessagesService: OutBoxMessagesService
  ) {
    super();
    this.mobileNumber = MobileNumberService.mobileNumber;
  }

  ngOnInit() {
    super.ngOnInit();
    this.pageConfig.showAddBtn = false;
    this.pageConfig.showHelpBtn = false;
    this.pageConfig.showAuditBtn = false;
    this.pageConfig.showRefreshBtn = true;
  }

  getEnumsValues() {
    this.getEnumValuesApi('ContactCreateType')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.ContactCreateTypeEnumValues = values));
    this.getEnumValuesApi('GenderType')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.GenderTypeEnumValues = values));
    this.getEnumValuesApi('PhonebookTypes')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.PhonebookTypeEnumValues = values));
  }

  configTableColumns() {
    this.tableColumns = [
      {
        title: ``,
        name: '',
        hasSearch: false,
        noSorting: true,
        isCheckBox: true,
      },
      {
        title: this.localizer.firstName,
        name: 'firstName',
        noSorting: false,
        hasSearch: true,
      },
      {
        title: this.localizer.lastName,
        name: 'lastName',
        hasSearch: true,
      },
      {
        title: this.localizer.mobile,
        name: 'cellPhone',
        hasSearch: true,
      },
      {
        title: `common.phone`,
        name: 'telephone',
        hasSearch: true,
      },
      {
        title: `common.phonebook`,
        name: 'phonebookName',
        hasSearch: true,
      },
      {
        hasSearch: true,
        name: 'genderType',
        title: `common.gender`,
        type: 'enum',
        enumName: 'GenderType',
      },
      {
        hasSearch: true,
        name: 'contactCreateType',
        title: `${this.tableVocabs}contactCreateType`,
        type: 'enum',
        enumName: 'ContactCreateType',
      },
      {
        hasSearch: true,
        name: 'phonebookTypes',
        title: `${this.tableVocabs}phonebookTypes`,
        type: 'enum',
        enumName: 'PhonebookTypes',
      },
      {
        title: this.localizer.createDate,
        name: 'createdAt',
        hasSearch: true,
        type: 'dateTime',
      },
    ];
  }

  setDefaultTableSort() {
    try {
      this.sortObject.push({ columnName: 'createdAt', sort: 1 });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  //#region Api

  //Get All
  getPaginationApi(): Observable<CustomResultOfPaginatedListOfContactsByCellPhoneDto> {
    return this.currentService.contactsGetContactsByCellPhone({
      body: {
        ...this.getBaseModelWithPaginationParams(),
        cellPhone: this.mobileNumber,
      },
    });
  }

  deleteContactsApi(ids: string[]) {
    this.unSubs();
    this.pageConfig.loading = true;
    this.currentService
      .contactsDeleteContacts({ body: { ids: ids } })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.selectedIds = []))
      )
      .subscribe(
        (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({ result });
          if (result.isSucceed) {
            this.selectedIds = [];
            this.getTableData();
          }
        },
        (error) => {
          ConsoleService.error('error', error);
          this.selectedIds = [];
        }
      );
  }

  //Delete
  deleteApi(id: string, phonebookId: number) {
    this.pageConfig.loading = true;

    this.currentService
      .contactsDelete({ body: { id: id, phonebookId: phonebookId } })
      .pipe(this.basePipeOperators())
      .subscribe(
        (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({ result });
          if (result.isSucceed) {
            this.getTableData();
            this.selectedIds = [];
          }
        },
        (error) => ConsoleService.error('error', error),
        () => (this.pageConfig.loading = false)
      );
  }
  //#endregion

  onDelete(
    id: any,
    phonebookId: number,
    content: any,
    confirmByWritingText?: boolean
  ) {
    const dialogResult = this.getConfirmDialog({
      content: content,
      isConfirmDelete: true,
      confirmByWritingText: confirmByWritingText,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.pageConfig.loading = true;
          this.deleteApi(id, phonebookId);
        }
      });
  }

  callGroupDeletion() {
    this.deleteContactsApi(this.selectedIds);
  }

  onGroupEdit() {
    if (this.selectedIds?.length === 0) return;
    this.pageConfig.isVisibleForm = true;
  }

  onUpdate(id?) {
    this.selectedIds.push(id);
    this.onGroupEdit();
  }

  override groupDeletionConfirmation(ids: number[]) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.deleteMessage',
      content: this.mobileNumber,
      isConfirmDelete: true,
      wordBreak: false,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.callGroupDeletion();
          this.selectedIds = [];
          this.unCheckSelectAll();
        }
      });
  }

  //#region select items
  setSelectedContactsFromEdit(item) {
    this.selectedContacts = [];

    this.selectedContacts?.push({
      contactId: item.id,
      phonebookId: item.phonebookId,
    });
  }

  onAddSelectedContact(
    event,
    item: ContactsByCellPhoneDto,
    element,
    isFromEditBtn
  ) {
    try {
      if (isFromEditBtn) {
        this.setSelectedContactsFromEdit(item);
        return;
      }
      const checkBoxAllElement =
        this.elementRef.nativeElement.querySelectorAll(`.select-all`);

      let checked = event?.target?.checked;
      if (!item) return;
      if (checked) {
        //add
        this.isSelectAll(checkBoxAllElement);
        if (this.selectedIds?.find((id) => id === item.id)) return;
        this.selectedIds?.push(item.id);
        this.selectedContacts?.push({
          contactId: item.id,
          phonebookId: item.phonebookId,
        });
        (element as HTMLElement).classList.add('row-active');
      } else {
        //remove
        const id = this.selectedIds?.findIndex((id) => id === item.id);
        if (id != -1) {
          this.selectedIds?.splice(id, 1);
          this.selectedContacts?.splice(id, 1);
        }

        (element as HTMLElement).classList.remove('row-active');
        if ((checkBoxAllElement[0] as HTMLInputElement).checked)
          this.unCheckSelectAll();
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  //#endregion
}
